import { Fragment, useEffect, useId, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { focusMainLandmark } from '@/lib/focus-main';
import { MustardSproutIcon, OliveBranchIcon } from './GardenBloomIcons';
import styles from './PraiseOrPrayGate.module.css';

export type ComposeMode = 'praise' | 'prayer';

interface ChoiceProps {
  value: ComposeMode | null;
  onChoose: (mode: ComposeMode) => void;
  required?: boolean;
  disabled?: boolean;
  choiceRef?: RefObject<HTMLDivElement | null>;
}

/** The same explicit choice remains beside restored work, without storing a new key. */
export function EntryModeChoice({ value, onChoose, required, disabled, choiceRef }: ChoiceProps) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div ref={choiceRef} className={styles.compact} tabIndex={-1}
      role="group" aria-label={t('entry.chooseMode')} aria-describedby={required ? id : undefined}>
      <div className={styles.compactButtons}>
        {(['praise', 'prayer'] as const).map(mode => (
          <button key={mode} type="button" disabled={disabled} aria-pressed={value === mode}
            onClick={() => onChoose(mode)}>{t(`entry.${mode}Action`)}</button>
        ))}
      </div>
      {required && <p id={id} role="alert">{t('entry.chooseBeforeSave')}</p>}
    </div>
  );
}

/**
 * The Home welcome: Praise or Pray, asked over the same Home view softly
 * blurred beneath it. "Garden Bloom" (JR's 2026-11-02 mockup): two round glass
 * circles, an olive branch for Praise (Psalm 52:8-9) and a sprouting mustard
 * seed for Pray (Matthew 17:20), joined by a thin decorative gold vine. The
 * circle alone is the button (clipped to a circle, so its corners are not
 * tappable); its label and subtitle are plain text under it. The halo and the
 * focus ring are siblings drawn around the circle, never part of the hit area.
 * It is a group, not a modal dialog: Home itself is made
 * inert by its owner (PrayPage) while the question is open, except the garden
 * pill and the Bible corner, which stay crisp, reachable and tappable above
 * the scrim. Choosing
 * confirms on the tapped button (press scale plus a gold glow) while the
 * scrim fades; the owner has already handed the composer back by then, so
 * typing is never held behind the transition.
 */
export default function PraiseOrPrayGate({ selected, onChoose, onExited }: {
  selected: ComposeMode | null;
  onChoose: (mode: ComposeMode) => void;
  onExited: () => void;
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const praiseRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  // Keyboard users start on Praise; Tab reaches Pray and then the garden pill,
  // and Shift+Tab the Bible corner (the rest of Home is inert). Nothing traps
  // focus: there is no modal to escape from. A touch device gets no scripted
  // focus, so the welcome never opens with a focus ring; a screen reader then
  // reads in screen order: Read Bible (top left), Praise, Pray, garden pill.
  // When the welcome goes away without a choice (a notice, an Undo or a
  // recovery surface took its place) focus is handed to the main landmark
  // instead of being dropped on the body.
  useEffect(() => {
    if (!(typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches)) {
      praiseRef.current?.focus({ preventScroll: true });
    }
    const root = rootRef.current;
    return () => {
      if (root && document.activeElement && root.contains(document.activeElement)) focusMainLandmark();
    };
  }, []);
  return (
    <div ref={rootRef} className={`${styles.gate} ${selected ? styles.leaving : ''}`}
      role="group" aria-label={t('entry.chooseMode')} data-praise-pray-gate
      data-entry-gate-state={selected ? 'leaving' : 'open'}
      onTransitionEnd={event => {
        if (event.target === event.currentTarget && event.propertyName === 'opacity' && selected) onExited();
      }}>
      <div className={styles.stack}>
        {(['praise', 'prayer'] as const).map((mode, index) => (
          <Fragment key={mode}>
            {index === 1 && (
              // Decorative only: aria-hidden, no pointer events, scales with
              // the layout, never reaches toward the garden pill.
              <svg className={styles.vine} viewBox="0 0 40 60" aria-hidden="true" focusable="false">
                <path className={styles.vineGlow} d="M20 0 C27 13 13 21 20 31 C25.5 39 15.5 49 20 60" />
                <path className={styles.vinePath} pathLength={1}
                  d="M20 0 C27 13 13 21 20 31 C25.5 39 15.5 49 20 60" />
                <path className={styles.vineLeaf} d="M21 25 C24 21.6 28.6 20.6 32 21.4 C30 24.6 25.4 26.6 21 25Z" />
                <path className={styles.vineLeaf} d="M19.2 35.4 C16.2 32 11.6 31 8.2 31.8 C10.2 35 14.8 37 19.2 35.4Z" />
              </svg>
            )}
            <div className={[
              styles.entrance,
              selected === mode ? styles.entranceChosen : '',
              selected && selected !== mode ? styles.entranceDimmed : '',
            ].join(' ').trim()} style={{ animationDelay: `${index * 80}ms` }}>
              <button ref={mode === 'praise' ? praiseRef : undefined} type="button"
                className={[styles.circle, selected === mode ? styles.chosen : ''].join(' ').trim()}
                aria-label={t(`entry.${mode}Action`)}
                aria-describedby={`${id}-${mode}`} data-entry-choice={mode}
                disabled={selected !== null}
                onClick={() => onChoose(mode)}>
                {mode === 'praise' ? (
                  <>
                    <span className={styles.burst} aria-hidden="true" />
                    <OliveBranchIcon className={styles.icon} classes={{ leaves: styles.oliveLeaves, sheen: styles.oliveSheen }} />
                  </>
                ) : (
                  <MustardSproutIcon className={styles.icon} classes={{ unfurl: styles.sproutUnfurl, unfurlGlow: styles.sproutGlow }} />
                )}
              </button>
              {/* Drawn around the circle (after it, for the sibling selectors);
                  never hit-testable. */}
              <span className={styles.halo} aria-hidden="true" />
              <span className={styles.ring} aria-hidden="true" />
              {/* Plain text, not tappable. The button carries the name; the
                  subtitle is its description, so both stay out of the
                  reading order to avoid saying them twice. */}
              <span className={styles.label} aria-hidden="true">{t(`entry.${mode}Action`)}</span>
              <span id={`${id}-${mode}`} className={styles.subtitle} aria-hidden="true">{t(`entry.${mode}Subtitle`)}</span>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
